import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import classNames from 'classnames';
import { additionalPosterEnabled, carouselV2Enabled } from 'helpers/ConfigHelper';
import { urlIsExternal } from 'helpers/ContentHelper';
import { renderImage as renderDynamicImage, POSTER_IMAGE } from 'helpers/ImageHelper';
import Badge from 'components/Badge';
import PosterContent from './components/PosterContent';
import Style from './Poster.module.scss';

const READER_MS_DELAY = 2300;
const Poster = ({
  badge,
  breakpoint,
  descriptionLines,
  favoritesManager,
  hasOverlay,
  isHoverStateV2,
  isSeriesDetailAnonymous,
  item,
  itemIndex,
  link: route,
  maxItems,
  moreLink: moreRoute,
  noPreview,
  onPosterClick,
  overlayProps,
  overrideAdditionalPosterStyle,
  overrideTheme,
  posterAltText,
  posterImage,
  segmentPlaylistButtonAction,
  video,
}) => {
  const theme = overrideTheme || Style;
  const ariaLabelRef = useRef(null);

  useEffect(() => {
    const timerRef = setTimeout(() => {
      ariaLabelRef.current.ariaLabel = `${item.seriesName || item.name}, new`;
      ariaLabelRef.current.ariaHidden = false;
    }, READER_MS_DELAY);
    return () => {
      clearTimeout(timerRef);
    };
  }, [item.name, item.seriesName]);

  const renderImage = image => {
    if (!image) return null;
    const dynamicImage = renderDynamicImage({
      alt: posterAltText,
      breakpoint,
      className: theme.carouselPosterImage,
      imageType: POSTER_IMAGE,
      src: image,
    });
    if (route) {
      const redirect = urlIsExternal(route) ? { href: route } : { to: route };
      return (
        <div aria-hidden ref={ariaLabelRef} tab-index="0">
          <Link key={route} {...redirect} onClick={onPosterClick}>
            {dynamicImage}
            <Badge badge={badge} theme={theme} title={item?.seriesName} />
          </Link>
        </div>
      );
    }
    return dynamicImage;
  };
  const getCarouselPosterClasses = [
    additionalPosterEnabled() && !overrideAdditionalPosterStyle && theme.additionalPoster,
    isSeriesDetailAnonymous && theme.seriesDetailAnonymous,
  ];

  if (itemIndex === 0 && !carouselV2Enabled()) {
    getCarouselPosterClasses.push(theme.carouselPosterFirst);
  } else if (itemIndex === maxItems - 1) {
    getCarouselPosterClasses.push(theme.carouselPosterEnd);
    if (item.id === 'seeMore') {
      getCarouselPosterClasses.push(theme.invisible);
    }
  } else {
    getCarouselPosterClasses.push(theme.carouselPoster);
  }

  return (
    <div className={classNames(getCarouselPosterClasses)}>
      <PosterContent
        badge={badge}
        breakpoint={breakpoint}
        descriptionLines={descriptionLines}
        favoritesManager={favoritesManager}
        hasOverlay={hasOverlay}
        image={posterImage}
        isHoverStateV2={isHoverStateV2}
        item={item}
        moreRoute={moreRoute}
        noPreview={noPreview}
        onPosterClick={onPosterClick}
        overlayProps={overlayProps}
        renderImage={renderImage}
        route={route}
        segmentPlaylistButtonAction={segmentPlaylistButtonAction}
        theme={theme}
        video={video}
      />
    </div>
  );
};

Poster.defaultProps = {
  badge: null,
  breakpoint: null,
  descriptionLines: 5,
  favoritesManager: {},
  hasOverlay: false,
  isHoverStateV2: false,
  isSeriesDetailAnonymous: false,
  item: {},
  itemIndex: 0,
  link: '',
  maxItems: 0,
  moreLink: '',
  noPreview: false,
  onPosterClick: () => {},
  overlayProps: {},
  overrideTheme: null,
  posterImage: '',
  posterAltText: '',
  segmentPlaylistButtonAction: () => {},
  theme: {},
  video: null,
};

Poster.propTypes = {
  badge: PropTypes.string,
  breakpoint: PropTypes.string,
  descriptionLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  favoritesManager: PropTypes.shape({}),
  hasOverlay: PropTypes.bool,
  isHoverStateV2: PropTypes.bool,
  isSeriesDetailAnonymous: PropTypes.bool,
  item: PropTypes.shape({}),
  itemIndex: PropTypes.number,
  link: PropTypes.string,
  maxItems: PropTypes.number,
  moreLink: PropTypes.string,
  noPreview: PropTypes.bool,
  onPosterClick: PropTypes.func,
  overlayProps: PropTypes.shape({}),
  overrideAdditionalPosterStyle: PropTypes.bool,
  overrideTheme: PropTypes.shape({}),
  posterImage: PropTypes.string,
  posterAltText: PropTypes.string,
  segmentPlaylistButtonAction: PropTypes.func,
  theme: PropTypes.shape({}),
  video: PropTypes.string,
};

export default Poster;
